import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse
} from '@angular/common/http';
import { BehaviorSubject, Observable, throwError, EMPTY, from } from 'rxjs';
import {
  catchError,
  filter,
  switchMap,
  take,
  finalize
} from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { AppState } from '@iw-shared/store/state/app.state';
import { setNotification } from '@iw-shared/store/actions/global.actions';
import { logout, refreshTokenSuccess } from '@iw-shared/store/actions/authentication.action';
import { AuthenticationService } from '@iw-shared/core/services/authentication.service';
import {Router} from "@angular/router";

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private disableRequests = false;

  constructor(
    private authenticationService: AuthenticationService,
    private store: Store<AppState>,
    private router: Router
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.disableRequests) return EMPTY;

    // 1. Initialer Check: Muss der Token erneuert werden?
    const isExpired = this.authenticationService.isAccessTokenExpired(1);
    const hasToken = this.authenticationService.isAccessToken();

    if (hasToken && isExpired) {
      return this.handleTokenRefresh(request, next);
    }

    // 2. Normalen Request senden (mit injiziertem Token falls nötig)
    return next.handle(this.injectToken(request)).pipe(
      catchError((error) => {
        // Falls der Server trotzdem 401 liefert, obwohl wir dachten der Token sei okay
        if (error instanceof HttpErrorResponse && error.status === 401) {
          return this.handleTokenRefresh(request, next);
        }
        return throwError(() => error);
      })
    );
  }

  private handleTokenRefresh(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    console.log('Refresh gestartet...'); // Erscheint das?
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(false);

      // Wir holen den Endpoint aus dem Store und starten dann den Refresh
      return this.store.select(state => state.storage.APIEndpoint).pipe(
        take(1),
        switchMap(endpoint => {
          console.log('Nutze Endpoint:', endpoint);
          const refresh$ = (endpoint === 'account')
            ? this.authenticationService.refreshToken()
            : this.authenticationService.refreshTokenAccount();

          return refresh$;
        }),
        switchMap((refreshResponse: any) => {
          if (refreshResponse?.result) {
            console.log('Server Antwort erhalten:', refreshResponse);

            // Erfolg: Token-Daten extrahieren (je nach Endpoint Struktur)
            const data = endpointData(refreshResponse.result);
            console.log('Extrahierte Daten:', data);
            this.store.dispatch(refreshTokenSuccess({ accessExpiredIn: data.expiredIn }));

            this.isRefreshing = false;
            this.refreshTokenSubject.next(true);
            return next.handle(this.injectToken(request));
          } else {
            this.handleInvalidSession();
            return EMPTY;
          }
        }),
        catchError((err) => {
          this.handleInvalidSession();
          return throwError(() => err);
        }),
        finalize(() => {
          this.isRefreshing = false;
        })
      );
    } else {
      // Warten, bis der erste Request den Refresh fertig hat
      return this.refreshTokenSubject.pipe(
        filter(success => success),
        take(1),
        switchMap(() => next.handle(this.injectToken(request)))
      );
    }
  }

  // Hilfsfunktion, um den Header zu setzen (nutzt deine Service-Logik)
  injectToken(request: HttpRequest<any>): HttpRequest<any> {
    if (this.authenticationService.isInjectionToken()) {
      const injectToken = this.authenticationService.getInjectionToken();
      if (injectToken) {
        return request.clone({
          setHeaders: { Authorization: `Bearer ${injectToken}` }
        });
      }
    }
    // Falls dein Service den Access-Token über Cookies regelt (HttpClient schickt diese autom. mit),
    // musst du hier nichts weiter clonen.
    // Falls er im Header stehen muss, bräuchtest du eine Methode wie getAccessToken().
    return request;
  }

  private handleInvalidSession() {
    this.disableRequests = true;
    this.store.dispatch(logout());
    this.store.dispatch(setNotification({
      notification: 'Ihre Session ist abgelaufen. Bitte loggen Sie sich erneut ein.',
      notificationType: 'info'
    }));

// 3. Navigation statt Reload
    // Wir navigieren zum Login. Das löscht den State der Komponenten automatisch.
    this.router.navigate(['/login']).then(() => {
      // Sobald die Navigation fertig ist, können wir die Sperre aufheben
      this.disableRequests = false;
    });
  }
}

// Helper um die verschachtelten Pfade sauber zu lesen
function endpointData(result: any) {
  return result?.accountBackendUserTokens?.data || result?.userTokens?.data;
}
